export type LocalEmployee = {
  id: string;
  fullName: string;
  position: string;
  fixMonthlyGross: number;
  stiFraction: number; // stored as 0..1, UI inputs % (e.g., 25 -> 0.25)
  ltip: boolean;
  createdAt: string;
  updatedAt: string;
};

type DbConfig = {
  name: string;
  version: number;
  storeName: string;
};

const DB: DbConfig = {
  name: 'datacircle',
  version: 2,
  storeName: 'employees',
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB.name, DB.version);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB.storeName)) {
        db.createObjectStore(DB.storeName, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('position_gradings')) {
        db.createObjectStore('position_gradings', { keyPath: 'positionKey' });
      }
      if (!db.objectStoreNames.contains('employee_gradings')) {
        db.createObjectStore('employee_gradings', { keyPath: 'employeeId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
  });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function runTx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(DB.storeName, mode);
    const store = tx.objectStore(DB.storeName);
    const result = await fn(store);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
    return result;
  } finally {
    db.close();
  }
}

export async function listLocalEmployees(): Promise<LocalEmployee[]> {
  return runTx('readonly', async (store) => {
    const items = await reqToPromise(store.getAll());
    const arr = (items ?? []) as LocalEmployee[];
    return [...arr].sort((a, b) => {
      if (a.position !== b.position) return a.position.localeCompare(b.position);
      return a.fullName.localeCompare(b.fullName);
    });
  });
}

export async function upsertLocalEmployee(employee: LocalEmployee): Promise<void> {
  await runTx('readwrite', async (store) => {
    await reqToPromise(store.put(employee));
  });
}

export async function deleteLocalEmployee(id: string): Promise<void> {
  await runTx('readwrite', async (store) => {
    await reqToPromise(store.delete(id));
  });
}

